/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Dimension } from '#utils/UNSAFE_visTypes/common';

const LABEL_OFFSET = 2;

function getValueCoord(isHoriz: boolean, isRtl: boolean, isNegative: boolean, barDims: Dimension) {
  if (isHoriz) {
    return (isNegative && !isRtl) || (!isNegative && isRtl) ? barDims.x : barDims.x + barDims.width;
  }
  return isNegative ? barDims.height + barDims.y : barDims.y;
}

export function getLabelProps(
  isHoriz: boolean,
  isRtl: boolean,
  isNegative: boolean,
  barDims: Dimension,
  dataLabelPosition: 'insideBarEdge' | 'outsideBarEdge' | 'center',
  labelDims?: Dimension
) {
  if (!labelDims) {
    return {};
  }

  const valueCoord = getValueCoord(isHoriz, isRtl, isNegative, barDims);

  if (dataLabelPosition === 'insideBarEdge') {
    return {
      y: isHoriz
        ? barDims.y + barDims.height / 2
        : valueCoord + (isNegative ? -1 : 1) * (labelDims.height / 2 + LABEL_OFFSET + 2),
      x: isHoriz
        ? valueCoord -
          ((isNegative && !isRtl) || (!isNegative && isRtl) ? -1 : 1) *
            (labelDims.width / 2 + LABEL_OFFSET)
        : barDims.x + barDims.width / 2
    };
  }

  if (dataLabelPosition === 'outsideBarEdge') {
    return {
      y: isHoriz
        ? barDims.y + barDims.height / 2
        : valueCoord - (isNegative ? -1 : 1) * (labelDims.height / 2 + LABEL_OFFSET),
      x: isHoriz
        ? valueCoord +
          ((isNegative && !isRtl) || (!isNegative && isRtl) ? -1 : 1) *
            (labelDims.width / 2 + LABEL_OFFSET)
        : barDims.x + barDims.width / 2
    };
  }

  return {
    x: barDims.x + barDims.width / 2,
    y: barDims.y + barDims.height / 2
  };
}

export function canFitLabel(
  isHoriz: boolean,
  labelDims: Dimension,
  barDims: Dimension,
  labelPos: 'insideBarEdge' | 'outsideBarEdge' | 'center'
) {
  if (isHoriz) {
    if (labelDims.height > barDims.height) {
      return false;
    }
    if (labelPos != 'outsideBarEdge') {
      return labelDims.width < barDims.width;
    }
    return true;
  }

  if (labelDims.width > barDims.width) {
    return false;
  }

  if (labelPos != 'outsideBarEdge') {
    return labelDims.height < barDims.height;
  }
  return true;
}

export const getStackLabelProps = (
  groupCenterCoord: number,
  valueCoord: number,
  isNegative: boolean,
  isHoriz: boolean,
  isRtl: boolean,
  labelDims: Dimension
) => {
  return {
    y: isHoriz
      ? groupCenterCoord
      : valueCoord - (isNegative ? -1 : 1) * (labelDims.height / 2 + LABEL_OFFSET),
    x: isHoriz
      ? valueCoord +
        ((isNegative && !isRtl) || (!isNegative && isRtl) ? -1 : 1) *
          (labelDims.width / 2 + LABEL_OFFSET)
      : groupCenterCoord
  };
};
